import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { getPageBySlug } from "../Api/api";
import "./Css/home.css";
import { FaHistory, FaEye, FaBullseye, FaCogs, FaStar, FaUsers, FaPhoneAlt, FaUser, FaBuilding, FaCity, FaMapMarkerAlt, FaWhatsapp } from "react-icons/fa";
import { MdOutlineAlternateEmail } from "react-icons/md";
import { FaLocationDot } from "react-icons/fa6";
import { motion } from "framer-motion";
import HomeHero from "../Components/HomeHero";
import HomeSection2 from "../Components/HomeSection2";
import HomeSection3 from "../Components/HomeSection3";
import HomeSection4 from "../Components/HomeSection4";
import HomeSection6 from "../Components/HomeSection6";
import HomeSection5 from "../Components/HomeSection5";
import HomeTestimonials from "../Components/HomeTestimonials";
import HomeStats from "../Components/HomeStats";
import HomeContact from "../Components/HomeContact";
import OptimizedImage from "../Components/OptimizedImage";
import HomeAbout from "../Components/HomeAbout";

export default function DynamicPage() {
  const { slug: slugParam } = useParams();
  const { i18n } = useTranslation();
  const slug = slugParam || 'home';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const lang = i18n.language === "ar" ? "ar" : "en";
  const assetUrl = (p) => {
    if (!p) return "";
    const s = String(p);
    if (/^https?:\/\//i.test(s)) return s;
    const path = s.startsWith("/") ? s : `/${s}`;
    return `https://staging.via-app.ca/via_api${path}`;
  };

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getPageBySlug(slug, lang)
      .then((res) => {
        if (!res || !res.page) {
          setNotFound(true);
        } else {
          setData(res);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  const seo = useMemo(() => {
    const s = data?.seo;
    if (!s) return {};
    return {
      title: lang === "ar" ? s.titleAr || s.titleEn : s.titleEn || s.titleAr,
      description: lang === "ar" ? s.descriptionAr || s.descriptionEn : s.descriptionEn || s.descriptionAr,
      keywords: lang === "ar" ? s.keywordsAr || s.keywordsEn : s.keywordsEn || s.keywordsAr,
    };
  }, [data, lang]);

  const getTxt = (obj, enKey, arKey) =>
    lang === "ar" ? obj?.[arKey] || obj?.[enKey] : obj?.[enKey] || obj?.[arKey];

  const iconMap = {
    FaHistory: <FaHistory size={32} className="text-white" />,
    FaEye: <FaEye size={32} className="text-white" />,
    FaBullseye: <FaBullseye size={32} className="text-white" />,
    FaCogs: <FaCogs size={32} className="text-white" />,
    FaStar: <FaStar size={32} className="text-white" />,
    FaUsers: <FaUsers size={32} className="text-white" />,
    FaPhoneAlt: <FaPhoneAlt size={32} className="text-white" />,
    FaWhatsapp: <FaWhatsapp size={32} className="text-white" />,
    FaUser: <FaUser size={32} className="text-white" />,
    MdOutlineAlternateEmail: <MdOutlineAlternateEmail size={32} className="text-white" />,
    FaLocationDot: <FaLocationDot size={32} className="text-white" />,
    FaBuilding: <FaBuilding size={32} className="text-white" />,
  };

  /* --------------------------- Render Components --------------------------- */

  const Hero = () => (
    <div>
      <HomeHero hero={data?.hero || {}} inView={true} i18n={i18n} />
    </div>
  );

  const HeroSkeleton = () => {
    const phHero = {
      background: "",
      logo: "",
      color1: "#078AB8",
      color2: "#31B761",
      titleEn: "",
      titleAr: "",
      descriptionEn: "",
      descriptionAr: "",
      btnEn: "",
      btnAr: ""
    };
    return (
      <div>
        <HomeHero hero={phHero} inView={true} i18n={i18n} />
      </div>
    );
  };

  const TopCards = () => {
    const cards = (data?.cards || []).filter((c) => !c.sec_id);
    if (!cards.length) return null;
    return (
      <section className="py-5">
        <div className="container">
          <div className="row g-4">
            {cards.map((c) => (
              <div key={c.id} className="col-md-6 col-lg-4 text-center">
                <div className="info-card h-100">
                  <div className="icon-circle mb-3">
                    {c.image ? (
                      <img src={assetUrl(c.image)} alt="" style={{ width: 50, height: 50, borderRadius: 8 }} />
                    ) : (typeof c.icon === 'string' && (/^https?:\/\//i.test(c.icon) || c.icon.startsWith('/') || /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(c.icon))) ? (
                      <img src={assetUrl(c.icon)} alt="" style={{ width: 50, height: 50, borderRadius: 8 }} />
                    ) : (
                      iconMap[c.icon] || iconMap.FaStar
                    )}
                  </div>
                  <h4>{lang === "ar" ? (c.lang?.title || c.title_ar || c.title_en || "") : (c.lang?.title || c.title_en || c.title_ar || "")}</h4>
                  <p className="text-muted small">{lang === "ar" ? (c.lang?.text || c.description_ar || c.description_en || "") : (c.lang?.text || c.description_en || c.description_ar || "")}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const FormOnly = () => {
    const [formData, setFormData] = useState({
      fullName: "",
      companyName: "",
      email: "",
      phone: "",
      city: "",
      address: "",
      businessType: "",
      message: "",
    });
    const [errors, setErrors] = useState({});
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const validate = () => {
      const temp = {};
      if (!formData.fullName) temp.fullName = "Full Name is required";
      if (!formData.email) temp.email = "Email is required";
      if (!formData.message) temp.message = "Message is required";
      setErrors(temp);
      return Object.keys(temp).length === 0;
    };
    const handleSubmit = (e) => {
      e.preventDefault();
      if (!validate()) return;
      alert("Message sent successfully!");
      setFormData({ fullName: "", companyName: "", email: "", phone: "", city: "", address: "", businessType: "", message: "" });
      setErrors({});
    };
    const cities = ["Abu Dhabi", "Dubai", "Sharjah", "Umm Al-Quwain", "Fujairah", "Ras Al Khaimah"];
    const contactData = {
      titleEn: "Please fill the form with all required information and our team will reach out to you as soon as possible:",
      titleAr: "Veuillez remplir le formulaire avec toutes les informations requises, et notre équipe vous contactera dans les plus brefs délais.",
    };
    return (
      <section>
        <div className="contact-us-page">
          <section className="py-5" style={{ background: "#f0f4f8" }}>
            <div className="container">
              <div className="row g-4">
                <div className="col-lg-12">
                  <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }}>
                    <h3 className="mb-4 fs-6">{i18n.language === "en" ? contactData.titleEn : contactData.titleAr} </h3>
                    <form onSubmit={handleSubmit} className="p-4 rounded shadow-sm bg-white">
                      {[
                        { label: i18n.language === "en" ? "Full Name" : "Nom complet", name: "fullName", type: "text", icon: <FaUser />, placeholder: i18n.language === "en" ? "Enter your full name" : "Entrez votre nom complet" },
                        { label: i18n.language === "en" ? "Company Name" : "Nom de l’entreprise", name: "companyName", type: "text", icon: <FaBuilding />, placeholder: i18n.language === "en" ? "Enter your company name" : "Entrez le nom de votre entreprise" },
                        { label: i18n.language === "en" ? "Email" : "Email", name: "email", type: "email", icon: <MdOutlineAlternateEmail />, placeholder: i18n.language === "en" ? "Enter your email" : "Entrez votre email" },
                        { label: i18n.language === "en" ? "Phone Number" : "Numéro de téléphone", name: "phone", type: "tel", icon: <FaPhoneAlt />, placeholder: i18n.language === "en" ? "Enter your phone number" : "Entrez votre numéro de téléphone" },
                        { label: i18n.language === "en" ? "City" : "Ville", name: "city", type: "text", icon: <FaCity />, placeholder: i18n.language === "en" ? "Enter your city" : "Entrez votre ville" },
                        { label: i18n.language === "en" ? "Address" : "Adresse", name: "address", type: "text", icon: <FaMapMarkerAlt />, placeholder: i18n.language === "en" ? "Enter your address" : "Entrez votre adresse" },
                        { label: i18n.language === "en" ? "Business Type" : "Type d’activité", name: "businessType", type: "text", icon: <FaBuilding />, placeholder: i18n.language === "en" ? "Enter your business type" : "Entrez votre type d’activité" },
                      ].map((field) => (
                        <div className="mb-3 position-relative" key={field.name}>
                          <label className="form-label">{field.label}</label>
                          <div className="input-icon-wrapper">
                            {field.icon && <span className="input-icon">{field.icon}</span>}
                            {field.type === "select" ? (
                              <select name={field.name} value={formData[field.name]} onChange={handleChange} className={`form-control ps-5 ${errors[field.name] ? "is-invalid" : ""}`} required>
                                <option value="">Select City</option>
                                {cities.map((city, i) => (
                                  <option key={i} value={city}>{city}</option>
                                ))}
                              </select>
                            ) : (
                              <input type={field.type} name={field.name} value={formData[field.name]} onChange={handleChange} placeholder={field.placeholder} className={`form-control ps-5 ${errors[field.name] ? "is-invalid" : ""}`} />
                            )}
                            {errors[field.name] && <div className="invalid-feedback">{errors[field.name]}</div>}
                          </div>
                        </div>
                      ))}
                      <div className="mb-3 position-relative">
                        <label className="form-label">{i18n.language === "en" ? "Your Message" : "Envoyer"}</label>
                        <div className="input-icon-wrapper">
                          <textarea name="message" value={formData.message} onChange={handleChange} placeholder={i18n.language === "en" ? "Type your message" : "Écrivez votre message"} className={`form-control ps-5 ${errors.message ? "is-invalid" : ""}`} rows="5" required></textarea>
                          {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                        </div>
                      </div>
                      <button type="submit" className="btn btn-gradient w-100">{i18n.language === "en" ? "Submit" : "Envoyer"}</button>
                    </form>
                  </motion.div>
                </div>
              </div>
            </div>
            <style>{`
              .btn-gradient { background: linear-gradient(90deg, #0077b6, #00b4d8); color: white; font-weight: 600; border: none; transition: all 0.3s ease; }
              .btn-gradient:hover { transform: translateY(-3px); box-shadow: 0 6px 15px rgba(0,0,0,0.2); background: linear-gradient(90deg, #00b4d8, #0077b6); }
              .input-icon-wrapper { position: relative; }
              .input-icon { position: absolute; top: 50%; left: 15px; transform: translateY(-50%); color: #0077b6; }
            `}</style>
          </section>
        </div>
      </section>
    );
  };

  const FormWithText = ({ contactData }) => {
    const [formData, setFormData] = useState({
      fullName: "",
      companyName: "",
      email: "",
      phone: "",
      city: "",
      address: "",
      request: "",
    });
    const [errors, setErrors] = useState({});
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const validate = () => {
      const temp = {};
      if (!formData.fullName) temp.fullName = "Full Name is required";
      if (!formData.email) temp.email = "Email is required";
      setErrors(temp);
      return Object.keys(temp).length === 0;
    };
    const handleSubmit = (e) => {
      e.preventDefault();
      if (!validate()) return;
      alert("Message sent successfully!");
      setFormData({ fullName: "", companyName: "", email: "", phone: "", city: "", address: "", request: "" });
      setErrors({});
    };
    const cities = ["Abu Dhabi", "Dubai", "Sharjah", "Umm Al-Quwain", "Fujairah", "Ras Al Khaimah"];
    const requestOptions = ["Quotation", "Demo"];
    return (
      <div id="contact-us-section6" className="contact-us-page mt-5 pt-5">
        <section className="py-5" style={{ background: "#f0f4f8" }}>
          <div className="container">
            <div className="row g-4">
              <div className="col-lg-6">
                <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }}>
                  <form onSubmit={handleSubmit} className="p-4 rounded shadow-sm bg-white">
                    {[
                      { label: i18n.language === "en" ? "Full Name" : "Nom complet", name: "fullName", type: "text", icon: <FaUser />, placeholder: i18n.language === "en" ? "Enter your full name" : "Entrez votre nom complet" },
                      { label: i18n.language === "en" ? "Company Name" : "Nom de l’entreprise", name: "companyName", type: "text", icon: <FaBuilding />, placeholder: i18n.language === "en" ? "Enter your company name" : "Entrez le nom de votre entreprise" },
                      { label: i18n.language === "en" ? "Email" : "Email", name: "email", type: "email", icon: <MdOutlineAlternateEmail />, placeholder: i18n.language === "en" ? "Enter your email" : "Entrez votre email" },
                      { label: i18n.language === "en" ? "Phone Number" : "Numéro de téléphone", name: "phone", type: "tel", icon: <FaPhoneAlt />, placeholder: i18n.language === "en" ? "Enter your phone number" : "Entrez votre numéro de téléphone" },
                      { label: i18n.language === "en" ? "City" : "Ville", name: "city", type: "text", icon: <FaCity />, placeholder: i18n.language === "en" ? "Enter your city" : "Entrez votre ville" },
                      { label: i18n.language === "en" ? "Address" : "Adresse", name: "address", type: "text", icon: <FaMapMarkerAlt />, placeholder: i18n.language === "en" ? "Enter your address" : "Entrez votre adresse" },
                      { label: i18n.language === "en" ? "Request" : "Demande", name: "request", type: "select", icon: <FaBuilding />, options: requestOptions },

                    ].map((field) => (
                      <div className="mb-3 position-relative" key={field.name}>
                        <label className="form-label">{field.label}</label>
                        <div className="input-icon-wrapper">
                          {field.icon && <span className="input-icon">{field.icon}</span>}
                          {field.type === "select" ? (
                            <select name={field.name} value={formData[field.name]} onChange={handleChange} className={`form-control ps-5 ${errors[field.name] ? "is-invalid" : ""}`} required>
                              <option value="">Select {field.label}</option>
                              {(field.options || []).map((option, i) => (
                                <option key={i} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <input type={field.type} name={field.name} value={formData[field.name]} onChange={handleChange} placeholder={field.placeholder} className={`form-control ps-5 ${errors[field.name] ? "is-invalid" : ""}`} />
                          )}
                          {errors[field.name] && <div className="invalid-feedback">{errors[field.name]}</div>}
                        </div>
                      </div>
                    ))}
                    <div className="mb-3 position-relative">
                      <div className="input-icon-wrapper">
                        {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                      </div>
                    </div>
                    <button type="submit" className="btn btn-gradient w-100">{i18n.language === "en" ? "Submit" : " Envoyer"}</button>
                  </form>
                </motion.div>
              </div>
              <div className="col-lg-6">
                <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1 }}>
                  <h3 className="mb-4 fw-bold">{i18n.language === "en" ? contactData.titleEn : contactData.titleAr} </h3>
                  <p>{i18n.language === "en" ? contactData.descriptionEn : contactData.descriptionAr} </p>
                  <p className="mt-4">{i18n.language === "en" ? contactData.instructionsEn : contactData.instructionsAr}</p>
                </motion.div>
              </div>
            </div>
          </div>
          <style>{`
            .btn-gradient { background: linear-gradient(90deg, #0077b6, #00b4d8); color: white; font-weight: 600; border: none; transition: all 0.3s ease; }
            .btn-gradient:hover { transform: translateY(-3px); box-shadow: 0 6px 15px rgba(0,0,0,0.2); background: linear-gradient(90deg, #00b4d8, #0077b6); }
            .input-icon-wrapper { position: relative; }
            .input-icon { position: absolute; top: 50%; left: 15px; transform: translateY(-50%); color: #0077b6; }
          `}</style>
        </section>
      </div>
    );
  };

  const getSectionStyle = (s) => {
    if (Number(s?.use_bg_color) === 1 && s?.bg_color1 && s?.bg_color2) {
      return { background: `linear-gradient(to right, ${s.bg_color1}, ${s.bg_color2})` };
    }
    return null;
  };

  const SolutionsIntro = ({ s }) => (
    s ? (
      <section className="py-5 w-75 m-auto rounded-5 shadow mt-5 mb-5" style={getSectionStyle(s) || { background: "linear-gradient(rgba(226, 247, 255, 0.9), rgba(240, 255, 240, 0.7))" }}>
        <div className="container mw-100 mtb plr mobMarT mobMarB ar_rtl_right">
          <div className="row align-items-center">
            <motion.div className="col-sm-2" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h1 className="mainHeading">{i18n.language === "en" ? s.labelEn : s.labelAr}</h1>
            </motion.div>
            <motion.div className="col-sm-10" style={{ paddingRight: "25px" }} initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }}>
              <div className="para-about">
                <p>
                  <strong>{i18n.language === "en" ? s.titleEn : s.titleAr}</strong>
                  <br />
                  {i18n.language === "en" ? s.descriptionEn : s.descriptionAr}
                </p>
                <h5>
                  <strong>{i18n.language === "en" ? s.subtitleEn : s.subtitleAr}</strong>
                </h5>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    ) : null
  );

  const SolutionsModules = ({ s }) =>
    s ? (
      <section
        className="py-5 m-auto rounded-5"
        style={{ width: "85%", ...getSectionStyle(s) }}
      >
        <div className="container mw-100 mtb plr">
          <div className="text-center">
            <motion.h1
              className="mainHeading mb-2 fs-2"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {i18n.language === "en" ? s.maintitleEn : s.maintitleAr}
            </motion.h1>
          </div>

          <div className="row g-4">
            {(s.cards || []).map((module, index) => {
              // ✅ CONSOLE DEBUG
              console.log("CARD DEBUG:", {
                index,
                use_color: module?.use_color,
                color1: module?.color1,
                color2: module?.color2,
                color: module?.color,
                condition:
                  Number(module?.use_color) === 1 &&
                  module?.color1 &&
                  module?.color2,
              });

              const title =
                i18n.language === "en"
                  ? module?.titleEn
                  : (module?.titleAr || module?.titleEn);
              const description =
                i18n.language === "en"
                  ? module?.descriptionEn
                  : (module?.descriptionAr || module?.descriptionEn);

              return (
                <motion.div
                  className="col-12 col-md-6 col-lg-4"
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div
                    className="p-3 rounded-5 shadow h-100 d-flex flex-column"
                    style={{
                      ...(Number(module?.use_color) === 1 &&
                        module?.color1 &&
                        module?.color2
                        ? {
                          background: `linear-gradient(to right, ${module.color1}, ${module.color2})`,
                        }
                        : {
                          backgroundColor:
                            module?.color || "#ffffff",
                        }),
                      backdropFilter: "blur(5px)",
                      transition:
                        "transform 0.3s, box-shadow 0.3s",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="text-center mb-3"
                      style={{ transition: "transform 0.3s" }}
                    >
                      <img
                        src={assetUrl(module?.img)}
                        alt={title}
                        className="rounded-4"
                        style={{
                          width: 200,
                          height: 120,
                          objectFit: "cover",
                          objectPosition: "center",
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </div>

                    <h2
                      className="erpModHead text-center mb-2"
                      style={{ fontWeight: "600" }}
                    >
                      {title}
                    </h2>

                    <p className="erpModP text-secondary">
                      {description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    ) : null;

  const SolutionsIntegrations = ({ s }) => (
    s ? (
      <section className="py-5 rounded shadow  mt-5 pt-5" style={getSectionStyle(s) || { background: "linear-gradient(rgba(226, 247, 255, 0.9), rgba(240, 255, 240, 0.7))" }}>
        <div className="container">
          <div className="row align-items-center">
            <motion.div className="col-12 col-lg-4 mb-4 mb-lg-0" initial={{ opacity: 0, x: -100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h1 className="mainHeading mb-3"> {i18n.language === "en" ? s.title1En : s.title1Ar}</h1>
              <p className="text-secondary mb-3">{i18n.language === "en" ? s.description1En : s.description1Ar}</p>
            </motion.div>
            <motion.div className="col-12 col-lg-7 text-center ps-0 ps-lg-5 ms-0 ms-lg-5" initial={{ opacity: 0, x: 100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h1 className="mainHeading mb-3">{i18n.language === "en" ? s.title2En : s.title2Ar}</h1>
              <p className="text-secondary mb-3">{i18n.language === "en" ? s.description2En : s.description2Ar}</p>
            </motion.div>
          </div>
        </div>
      </section>
    ) : null
  );

  const SolutionsBenefits = ({ s }) => (
    s ? (
      <section className="py-5 rounded shadow  mt-5 pt-5" style={getSectionStyle(s) || { background: "linear-gradient(rgba(226, 247, 255, 0.9), rgba(240, 255, 240, 0.7))" }}>
        <div className="container">
          <div className="row align-items-center">
            <motion.div className="col-12 col-lg-6 mb-4 mb-lg-0" initial={{ opacity: 0, x: -100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h1 className="mainHeading mb-3">{i18n.language === "en" ? s.title1En : s.title1Ar}</h1>
              <p className="text-secondary mb-3">{i18n.language === "en" ? s.paragraph1En : s.paragraph1Ar}</p>
              <p className="text-secondary mb-3">{i18n.language === "en" ? s.paragraph2En : s.paragraph2Ar}</p>
              <ol className="ps-3">
                {(i18n.language === "en" ? s.stepsEn : s.stepsAr).map((benefit, index) => (
                  <motion.li key={index} className="mb-2 fw-bold text-dark" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} style={{ lineHeight: "1.8", fontSize: "16px" }}>
                    {benefit}
                  </motion.li>
                ))}
              </ol>
            </motion.div>
            <motion.div className="col-12 col-lg-6 text-center" initial={{ opacity: 0, x: 100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <OptimizedImage
                src={assetUrl(s.image)}
                alt="viaERP"
                className="img-fluid rounded shadow-lg"
                width={600}
                height={450}
                style={{ maxHeight: "450px", objectFit: "cover", transition: "transform 0.3s" }}
              />
            </motion.div>
          </div>
        </div>
      </section>
    ) : null
  );

  const AboutSection = ({ s }) => (
    s ? (
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center g-4">
            <motion.div className="col-12 col-lg-7" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h2 className="fw-bold mb-3">{i18n.language === "en" ? s.titleEn : s.titleAr}</h2>
              {s.subtitleEn || s.subtitleAr ? (
                <h5 className="text-muted mb-3">{i18n.language === "en" ? s.subtitleEn : s.subtitleAr}</h5>
              ) : null}
              <p className="text-secondary">{i18n.language === "en" ? s.descriptionEn : s.descriptionAr}</p>
            </motion.div>
            {s.img && (
              <motion.div className="col-12 col-lg-5 text-center" initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
                <OptimizedImage src={s.img} alt="about" className="img-fluid rounded shadow" width={540} height={360} style={{ objectFit: "cover" }} />
              </motion.div>
            )}
          </div>
        </div>
      </section>
    ) : null
  );

  const ServicesPoints = ({ s }) => {
    if (!s) return null;
    const points = (i18n.language === "en" ? s.descriptionEn : s.descriptionAr);
    const arr = Array.isArray(points) ? points : String(points || "").split(/\r?\n/).filter(Boolean);
    return (
      <section className="py-3 bg-light w-75 m-auto rounded-4 shadow mt-5 mb-5" style={getSectionStyle(s) || { background: "linear-gradient(rgba(209, 243, 255, 0.9), rgba(221, 255, 220, 0.7))" }}>
        <div className="container">
          <motion.h2 className="fs-4 fw-bold lh-lg" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            {i18n.language === "en" ? s.titleEn : s.titleAr}
          </motion.h2>
          <motion.h4 className="text-secondary fs-6 mb-3" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
            {i18n.language === "en" ? s.subtitleEn : s.subtitleAr}
          </motion.h4>
          <motion.ol className="custom-ol mx-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }}>
            {arr.map((point, index) => (
              <motion.li key={index} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }}>
                {point}
              </motion.li>
            ))}
          </motion.ol>
        </div>
      </section>
    );
  };

  const ServicesImagesGrid = ({ cards }) => {
    const list = Array.isArray(cards) ? cards : [];
    if (!list.length) return null;
    return (
      <section className="py-5 bg-light mt-5">
        <div className="container">
          <div className="row justify-content-center">
            {list.map((item, idx) => (
              <div key={idx} className="col-12 col-md-6 col-lg-4 d-flex align-items-stretch mb-4">
                <div className="card shadow-sm rounded-4 w-100 overflow-hidden border-0" style={{ transition: "all 0.3s ease" }}>
                  <div className="card-img-top" style={{ backgroundImage: `url(${assetUrl(item.background_image)})`, height: "220px", backgroundSize: "cover", backgroundPosition: "center", transition: "transform 0.4s ease" }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const ServicesDualTextSteps = ({ s }) => {
    if (!s) return null;
    const steps = i18n.language === "en" ? s.stepsEn : s.stepsAr;
    const bolds = i18n.language === "en" ? s.stepsboldEn : s.stepsboldAr;
    const stepsArr = Array.isArray(steps) ? steps : String(steps || "").split(/\r?\n/).filter(Boolean);
    const boldArr = Array.isArray(bolds) ? bolds : String(bolds || "").split(/\r?\n/).filter(Boolean);
    return (
      <section id="how-we-can-assist" className="py-5 bg-light rounded shadow" style={getSectionStyle(s)}>
        <div className="container">
          <div className="row align-items-center">
            <motion.div className="col-12 col-lg-6 mb-4 mb-lg-0" initial={{ opacity: 0, x: -100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h2 className="fw-bold mb-3">{i18n.language === "en" ? s.title1En : s.title1Ar}</h2>
              <p className="text-secondary mb-4">{i18n.language === "en" ? s.paragraph1En : s.paragraph1Ar}</p>
              <h2 className="fw-bold mb-3">{i18n.language === "en" ? s.title2En : s.title2Ar}</h2>
              <p className="text-secondary">{i18n.language === "en" ? s.paragraph2En : s.paragraph2Ar}</p>
              <ol className="mt-3 ps-3">
                {stepsArr.map((step, i) => (
                  <li key={i} className="mb-2">
                    <span className="fw-bold">{boldArr[i]}</span> {step}
                  </li>
                ))}
              </ol>
            </motion.div>
            <motion.div className="col-12 col-lg-6 text-center" initial={{ opacity: 0, x: 100 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <img src={s.image} alt="ERP Solutions" className="img-fluid rounded shadow-lg" style={{ maxHeight: "450px", objectFit: "cover" }} />
            </motion.div>
          </div>
        </div>
      </section>
    );
  };

  const TextAndItem = ({ s }) => {
    const isEn = i18n.language === "en";
    const title = isEn ? s.titleEn : s.titleAr;
    const subtitle = isEn ? s.subtitleEn : s.subtitleAr;
    const items = isEn ? (Array.isArray(s.itemsEn) ? s.itemsEn : String(s.descriptionEn || "").split(/\r?\n/).filter(Boolean)) : (Array.isArray(s.itemsAr) ? s.itemsAr : String(s.descriptionAr || "").split(/\r?\n/).filter(Boolean));
    return (
      <section className="py-5 position-relative mt-5" style={{ background: "linear-gradient(135deg, #0071BD 0%, #1b7228ff 100%)", color: "#fff" }}>
        <div className="container">
          <div className="row align-items-center">
            <motion.div className="col-lg-12" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h1 className="fw-bold mb-3 fs-2" style={{ lineHeight: 1.3 }}>{title}</h1>
              {subtitle ? <h5 className="opacity-75 mb-4">{subtitle}</h5> : null}
              <ul className="list-unstyled">
                {items.map((item, idx) => (
                  <motion.li key={`${item}-${idx}`} className="mb-2 d-flex align-items-start" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                    <span className="me-2" style={{ color: "#DCC527", fontSize: "1.1rem" }}>•</span>
                    {item}
                  </motion.li>
                ))}
              </ul>
              <motion.a href="#contact" className="btn mt-4 px-4 py-2 fw-semibold fw-bold" whileHover={{ scale: 1.05 }} style={{ background: "linear-gradient(90deg, rgb(1 126 255), rgb(96 188 103))", color: "#fff", borderRadius: "30px", boxShadow: "0 4px 10px rgba(0,0,0,0.2)" }}>
                Contact Us
              </motion.a>
            </motion.div>
          </div>
        </div>
      </section>
    );
  };

  /* Text Section */
  const TextSection = (s, i) => (
    <section
      key={i}
      className="py-5"
      style={s?.background_image ? { backgroundImage: `url(${s.background_image})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
    >
      <div className="container">
        {s?.lang?.title && <h2 className="fw-bold mb-3">{s.lang.title}</h2>}
        {s?.lang?.subtitle && <p className="text-muted mb-3">{s.lang.subtitle}</p>}
        {s?.lang?.description && <p>{s.lang.description}</p>}
      </div>
    </section>
  );

  /* Cards Section */
  const CardsSection = (s, i) => {
    const section3 = {
      titleHeadEn: s.title_en || s.lang?.title || "",
      titleHeadAr: s.title_ar || s.lang?.title_ar || "",
      btnEn: s.btn_text_en || s.lang?.btn_text || "",
      btnAr: s.btn_text_ar || s.lang?.btn_text_ar || "",
      cardData: (s.cards || []).map((c, idx) => ({
        id: c.id ?? idx,
        titleEn: c.title_en,
        titleAr: c.title_ar,
        descriptionEn: c.description_en,
        descriptionAr: c.description_ar,
        icon: c.image ? assetUrl(c.image) : (c.icon ? assetUrl(c.icon) : ""),
        btnLink: c.link || ""
      }))
    };
    return <HomeSection3 key={`cards-${i}`} section3={section3} servicesInView={true} i18n={i18n} />;
  };

  const normalizedSections = useMemo(() => {
    const allCards = data?.cards || [];
    const secs = (data?.sections || [])
      .sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0))
      .map((s) => {
        const sectionCards = allCards.filter((c) => c.sec_id === s.id);
        // const sectionCards = (Array.isArray(s.cards) && s.cards.length > 0)
        //   ? s.cards
        //   : allCards.filter((c) => c.sec_id === s.id);

        return {
          ...s,
          cards: sectionCards,
        };
      });
    return secs.map((s, i) => {
      const type = (s.type || s.component || "").toLowerCase();

      // 1. Services / Cards
      if (type === "section3" || type === "services_section" || type === "cards") {
        const section3 = {
          titleHeadEn: s.lang?.title || s.title_en || "",
          titleHeadAr: s.lang?.title || s.title_ar || "",
          btnEn: s.lang?.btn_text || s.btn_text_en || "",
          btnAr: s.lang?.btn_text || s.btn_text_ar || "",
          use_bg_color: Number(s.use_bg_color ?? 0),
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
          cardData: (s.cards || []).map((c, idx) => ({
            id: c.id ?? idx,
            titleEn: c.lang?.title || c.title_en || "",
            titleAr: c.lang?.title || c.title_ar || "",
            descriptionEn: c.lang?.text || c.description_en || "",
            descriptionAr: c.lang?.text || c.description_ar || "",
            icon: c.image ? assetUrl(c.image) : (c.icon ? assetUrl(c.icon) : ""),
            btnLink: c.link || "",
            use_color: c.use_color,
            color1: c.color1,
            color2: c.color2,
            color: c.color,
          }))
        };
        return { key: `services-${i}`, id: s.id, type: "services", payload: section3 };
      }

      // 2. Solutions
      // if (type === "section4" || type === "solutions_section") {
      //   const section4 = {
      //     titleHeadEn: s.title_en || s.lang?.title || "",
      //     titleHeadAr: s.title_ar || s.lang?.title_ar || "",
      //     btnEn: s.btn_text_en || s.lang?.btn_text || "",
      //     btnAr: s.btn_text_ar || s.lang?.btn_text_ar || "",
      //     cardData: (s.cards || []).map((c, idx) => ({
      //       id: c.id ?? idx,
      //       titleEn: c.title_en || c.lang?.title || "",
      //       titleAr: c.title_ar || c.lang?.title || "",
      //       descriptionEn: c.description_en || c.lang?.text || "",
      //       descriptionAr: c.description_ar || c.lang?.text || "",
      //       img: c.image ? assetUrl(c.image) : "",
      //       icon: c.icon ? assetUrl(c.icon) : "",
      //       lastUpdated: c.lastUpdated || "",
      //       btnLink: c.link || ""
      //     }))
      //   };
      //   return { key: `solutions-${i}`, type: "solutions", payload: section4 };
      // }

      if (type === "section4" || type === "solutions_section") {
        const section4 = {
          titleHeadEn: s.lang?.title || s.title_en || "",
          titleHeadAr: s.lang?.title || s.title_ar || "",
          btnEn: s.lang?.btn_text || s.btn_text_en || "",
          btnAr: s.lang?.btn_text || s.btn_text_ar || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
          cardData: (s.cards || []).map((c, idx) => ({
            id: c.id ?? idx,
            titleEn: c.lang?.title || c.title_en || "",
            titleAr: c.lang?.title || c.title_ar || "",
            descriptionEn: c.lang?.text || c.description_en || "",
            descriptionAr: c.lang?.text || c.description_ar || "",
            img: c.image ? assetUrl(c.image) : "",
            icon: c.icon ? assetUrl(c.icon) : "",
            lastUpdated: c.lastUpdated || "",
            btnLink: c.link || "",
            use_color: Number(c.use_color ?? 0),
            color1: c.color1,
            color2: c.color2,
            color: c.color,
          }))
        };
        return { key: `solutions-${i}`, id: s.id, type: "solutions", payload: section4 };
      }

      if (type === "solutions_intro") {
        const l = s.lang || {};
        const intro = {
          labelEn: l.btn_text || "",
          labelAr: l.btn_text || "",
          titleEn: l.title || "",
          titleAr: l.title || "",
          descriptionEn: l.description || "",
          descriptionAr: l.description || "",
          subtitleEn: l.subtitle || "",
          subtitleAr: l.subtitle || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,

        };
        return { key: `solutions-intro-${i}`, id: s.id, type: "solutions_intro", payload: intro };
      }

      if (type === "solutions_modules") {
        const l = s.lang || {};
        const mod = {
          maintitleEn: l.title || "",
          maintitleAr: l.title || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
          cards: (s.cards || []).map((c, idx) => ({
            id: c.id ?? idx,
            titleEn: c.title_en || c.lang?.title || c.titleEn || "",
            titleAr: c.title_ar || c.lang?.title || c.titleAr || "",
            descriptionEn: c.description_en || c.lang?.description || c.lang?.text || c.descriptionEn || c.description || c.text || "",
            descriptionAr: c.description_ar || c.description_fr || c.lang?.description || c.lang?.text || c.descriptionAr || c.descriptionFr || c.description || c.text || "",
            img: c.image ? assetUrl(c.image) : c.img ? assetUrl(c.img) : "",
            color: c.color || "#ffffff",
            use_color: c.use_color,
            color1: c.color1,
            color2: c.color2,
          }))
        };
        return { key: `solutions-modules-${i}`, id: s.id, type: "solutions_modules", payload: mod };
      }

      if (type === "solutions_integrations") {
        const l = s.lang || {};
        const partsEn = String(l.description || "").split(/\r?\n---\r?\n|\r?\n---|---/);
        const integ = {
          title1En: l.title || "",
          title1Ar: l.title || "",
          description1En: partsEn[0] || "",
          description1Ar: partsEn[0] || "",
          title2En: l.subtitle || "",
          title2Ar: l.subtitle || "",
          description2En: partsEn[1] || "",
          description2Ar: partsEn[1] || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
        };
        return { key: `solutions-integrations-${i}`, id: s.id, type: "solutions_integrations", payload: integ };
      }

      if (type === "about") {
        const l = s.lang || {};
        const payload = {
          titleEn: l.title || s.title_en || "",
          titleAr: l.title_ar || s.title_ar || "",
          subtitleEn: l.subtitle || s.subtitle_en || "",
          subtitleAr: l.subtitle_ar || s.subtitle_ar || "",
          descriptionEn: l.description || s.description_en || "",
          descriptionAr: l.description_ar || s.description_ar || "",
          img: s.image ? assetUrl(s.image) : s.background_image ? assetUrl(s.background_image) : "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,

        };
        return { key: `about-${i}`, id: s.id, type: "about", payload };
      }

      if (type === "contact" || type === "contact_section") {
        const l = s.lang || {};
        const payload = {
          titleEn: l.title || s.title_en || "",
          titleAr: l.title_ar || s.title_ar || "",
          subtitleEn: l.subtitle || s.subtitle_en || "",
          subtitleAr: l.subtitle_ar || s.subtitle_ar || "",
          btnTextEn: l.btn_text || s.btn_text_en || "",
          btnTextAr: l.btn_text_ar || s.btn_text_ar || "",
          background: s.background_image ? assetUrl(s.background_image) : (s.image ? assetUrl(s.image) : ""),
          // color1: s.stepsbold_en || l.stepsbold || "#00202c",
          // color2: s.stepsbold_ar || l.stepsbold_ar || "#002634",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,

        };
        return { key: `contact-${i}`, id: s.id, type: "contact", payload };
      }

      if (type === "solutions_benefits") {
        const l = s.lang || {};
        const steps = String(l.steps || "").split(/\r?\n/).filter(Boolean);
        const ben = {
          title1En: l.title || "",
          title1Ar: l.title || "",
          paragraph1En: l.paragraphcard1 || "",
          paragraph1Ar: l.paragraphcard1 || "",
          paragraph2En: l.description || "",
          paragraph2Ar: l.description || "",
          stepsEn: steps,
          stepsAr: steps,
          image: s.image ? assetUrl(s.image) : s.img ? assetUrl(s.img) : "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
        };
        return { key: `solutions-benefits-${i}`, id: s.id, type: "solutions_benefits", payload: ben };
      }
      if (type === "services_points") {
        const t = s.lang || {};
        const payload = {
          titleEn: t.title || "",
          titleAr: t.title || "",
          subtitleEn: t.subtitle || "",
          subtitleAr: t.subtitle || "",
          descriptionEn: t.description || "",
          descriptionAr: t.description || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,

        };
        return { key: `services-points-${i}`, id: s.id, type: "services_points", payload };
      }
      if (type === "services_images_grid") {
        const cards = (s.cards || []).map((c) => ({ background_image: c.background_image || c.image || c.img || "" }));
        return { key: `services-images-grid-${i}`, id: s.id, type: "services_images_grid", payload: cards };
      }
      if (type === "services_dual_text_steps") {
        const l = s.lang || {};
        const payload = {
          title1En: l.title || "",
          title1Ar: l.title || "",
          paragraph1En: l.paragraphcard1 || "",
          paragraph1Ar: l.paragraphcard1 || "",
          title2En: l.subtitle || "",
          title2Ar: l.subtitle || "",
          paragraph2En: l.description || "",
          paragraph2Ar: l.description || "",
          stepsEn: l.steps || "",
          stepsAr: l.steps || "",
          stepsboldEn: l.stepsbold || "",
          stepsboldAr: l.stepsbold || "",
          image: s.image ? assetUrl(s.image) : "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,

        };
        return { key: `services-dual-text-steps-${i}`, id: s.id, type: "services_dual_text_steps", payload };
      }

      if (type === "text_and_item") {
        const l = s.lang || {};
        const itemsEn = String(s.description_en || l.description || "").split(/\r?\n/).filter(Boolean);
        const itemsAr = String(s.description_ar || l.description || "").split(/\r?\n/).filter(Boolean);
        const payload = {
          titleEn: s.title_en || l.title || "",
          titleAr: s.title_ar || l.title || "",
          subtitleEn: s.subtitle_en || l.subtitle || "",
          subtitleAr: s.subtitle_ar || l.subtitle || "",
          itemsEn,
          itemsAr,
        };
        return { key: `text-and-item-${i}`, id: s.id, type: "text_and_item", payload };
      }

      if (type === "form") {
        return { key: `form-${i}`, id: s.id, type: "form", payload: {} };
      }

      if (type === "form_with_text") {
        const contactData = {
          titleEn: s.title_en || s.lang?.title || "",
          titleAr: s.title_ar || s.lang?.title_ar || "",
          descriptionEn: s.description_en || s.lang?.description || "",
          descriptionAr: s.description_ar || s.lang?.description_ar || "",
          instructionsEn: s.instructions_en || s.lang?.instructions || "",
          instructionsAr: s.instructions_ar || s.lang?.instructions_ar || "",
        };
        return { key: `form-text-${i}`, id: s.id, type: "form_with_text", payload: contactData };
      }

      // 3. Clients
      if (type === "section5" || type === "clients_section" || type === "clients") {
        const section5 = {
          titleHeadEn: s.title_en || s.lang?.title || "",
          titleHeadAr: s.title_ar || s.lang?.title_ar || "",
          clientsData: (s.cards || s.clients || []).map((c, idx) => ({
            id: c.id ?? idx,
            img: c.image ? assetUrl(c.image) : c.img ? assetUrl(c.img) : "",
            alt: (lang === "ar" ? (c.lang?.title || c.title_ar || "") : (c.lang?.title || c.title_en || "")) || c.alt || ""
          }))
        };
        return { key: `clients-${i}`, id: s.id, type: "clients", payload: section5 };
      }

      // 4. Testimonials
      if (type === "testimonials" || type === "testimonials_section") {
        const t = s.testimonials || {};
        let cards = (s.cards && s.cards.length > 0) ? s.cards : ((t.cards && t.cards.length > 0) ? t.cards : (data.testimonials?.cards || []));
        if (!Array.isArray(cards)) cards = [];

        const testimonials = {
          title: s.title_en || t.title || "",
          titleAr: s.title_ar || t.titleAr || "",
          subtitle: s.subtitle_en || t.subtitle || "",
          subtitleAr: s.subtitle_ar || t.subtitleAr || "",
          cards: cards.map((c, idx) => ({
            id: c.id ?? idx,
            avatar: c.image ? assetUrl(c.image) : c.avatar ? assetUrl(c.avatar) : "",
            name: c.lang?.title || c.title_en || c.name_en || c.name || "",
            nameAr: c.lang?.title || c.title_ar || c.name_ar || c.nameAr || "",
            role: c.lang?.subtitle || c.subtitle_en || c.role_en || c.role || "",
            roleAr: c.lang?.subtitle || c.subtitle_ar || c.role_ar || c.roleAr || "",
            comment: c.lang?.comment || c.comment_en || c.comment || "",
            commentAr: c.lang?.comment || c.comment_ar || c.commentAr || "",
            companyname: c.company || c.companyname || ""
          }))
        };
        return { key: `testimonials-${i}`, id: s.id, type: "testimonials", payload: testimonials };
      }

      // 5. Stats
      if (type === "stats" || type === "stats_section") {
        const st = s.stats || {};
        const items = s.cards && s.cards.length > 0 ? s.cards : (st.items || []);

        const stats = {
          title: s.title_en || st.title || "",
          titleAr: s.title_ar || st.titleAr || "",
          items: items.map((it, idx) => ({
            id: it.id ?? idx,
            number: it.number || it.description_en || "0",
            label: it.label || it.title_en || "",
            labelAr: it.labelAr || it.title_ar || "",
            color: it.color || "#0071BD"
          }))
        };
        return { key: `stats-${i}`, id: s.id, type: "stats", payload: stats };
      }

      // 6. Two Cards Text
      if (type === "section2" || type === "two_cards_text") {
        const section2 = {
          titlecard1En: s.title_en || s.lang?.title || "",
          titlecard1Ar: s.title_ar || s.lang?.title || "",
          paragraphcard1En: s.paragraphcard1_en || s.lang?.paragraphcard1 || s.description_en || "",
          paragraphcard1Ar: s.paragraphcard1_ar || s.lang?.paragraphcard1 || s.description_ar || "",
          btncardEn: s.btn_text_en || s.lang?.btn_text || "",
          btncardAr: s.btn_text_ar || s.lang?.btn_text || "",
          titlecard2En: s.stepsbold_en || s.lang?.stepsbold || "",
          titlecard2Ar: s.stepsbold_ar || s.lang?.stepsbold || "",
          paragraphcard2En: s.steps_en || s.lang?.steps || "",
          paragraphcard2Ar: s.steps_ar || s.lang?.steps || "",
          use_bg_color: s.use_bg_color,
          bg_color1: s.bg_color1,
          bg_color2: s.bg_color2,
        };
        return { key: `section2-${i}`, id: s.id, type: "section2", payload: section2 };
      }

      // 7. Text Only (Default)
      const section6 = {
        titleEn: s.lang?.title || s.title_en || "",
        titleAr: s.lang?.title || s.title_ar || "",
        descriptionEn: s.lang?.description || s.description_en || "",
        descriptionAr: s.lang?.description || s.description_ar || "",
        btnEn: s.lang?.btn_text || s.btn_text_en || "",
        btnAr: s.lang?.btn_text || s.btn_text_ar || "",
        img: s.image ? assetUrl(s.image) : s.background_image ? assetUrl(s.background_image) : "",
        alt: s.alt || "",
        use_bg_color: s.use_bg_color,
        bg_color1: s.bg_color1,
        bg_color2: s.bg_color2,
      };
      return { key: `text-${i}`, id: s.id, type: "text", payload: section6 };
    });
  }, [data]);

  const renderOneSection = (s) => {
    if (s.type === "services") return <HomeSection3 key={s.key} section3={s.payload} servicesInView={true} i18n={i18n} />;
    if (s.type === "solutions") return <HomeSection4 key={s.key} section4={s.payload} WeOfferInView={true} i18n={i18n} />;
    if (s.type === "clients") return <HomeSection5 key={s.key} section5={s.payload} ClientsInView={true} i18n={i18n} />;
    if (s.type === "section2") return <HomeSection2 key={s.key} section2={s.payload} section2InView={true} i18n={i18n} />;
    if (s.type === "text") return <HomeSection6 key={s.key} section6={s.payload} Section6InView={true} i18n={i18n} />;
    if (s.type === "services_points") return <ServicesPoints key={s.key} s={s.payload} />;
    if (s.type === "services_images_grid") return <ServicesImagesGrid key={s.key} cards={s.payload} />;
    if (s.type === "services_dual_text_steps") return <ServicesDualTextSteps key={s.key} s={s.payload} />;
    if (s.type === "solutions_intro") return <SolutionsIntro key={s.key} s={s.payload} />;
    if (s.type === "solutions_modules") return <SolutionsModules key={s.key} s={s.payload} />;
    if (s.type === "solutions_integrations") return <SolutionsIntegrations key={s.key} s={s.payload} />;
    if (s.type === "solutions_benefits") return <SolutionsBenefits key={s.key} s={s.payload} />;
    if (s.type === "text_and_item") return <TextAndItem key={s.key} s={s.payload} />;
    if (s.type === "testimonials") {
      const tObj = {
        title: i18n.language === "ar" ? s.payload.titleAr : s.payload.title,
        subtitle: i18n.language === "ar" ? s.payload.subtitleAr : s.payload.subtitle,
        cards: (s.payload.cards || []).map((c) => ({
          id: c.id,
          avatar: c.avatar,
          name: i18n.language === "ar" ? c.nameAr : c.name,
          role: i18n.language === "ar" ? c.roleAr : c.role,
          comment: i18n.language === "ar" ? c.commentAr : c.comment,
          companyname: c.companyname,
        })),
      };
      return <HomeTestimonials key={s.key} testimonials={tObj} i18n={i18n} />;
    }
    if (s.type === "about") {
      const about = {
        title: i18n.language === "en" ? s.payload.titleEn : s.payload.titleAr,
        subtitle: i18n.language === "en" ? s.payload.subtitleEn : s.payload.subtitleAr,
        description: i18n.language === "en" ? s.payload.descriptionEn : s.payload.descriptionAr,
        image: s.payload.img
      };
      return <HomeAbout key={s.key} about={about} />;
    }
    if (s.type === "contact") {
      const contact = {
        title: i18n.language === "en" ? s.payload.titleEn : s.payload.titleAr,
        subtitle: i18n.language === "en" ? s.payload.subtitleEn : s.payload.subtitleAr,
        btnText: i18n.language === "en" ? s.payload.btnTextEn : s.payload.btnTextAr,
        background: s.payload.background,
        bg_color1: s.payload.bg_color1,
        bg_color2: s.payload.bg_color2,
        use_bg_color: s.payload.use_bg_color,
      };
      return <HomeContact key={s.key} contact={contact} />;
    }
    if (s.type === "form") return <FormOnly key={s.key} />;
    if (s.type === "form_with_text") return <FormWithText key={s.key} contactData={s.payload} />;
    return null;
  };

  const renderSections = () => normalizedSections.map((s) => renderOneSection(s));

  const renderSectionAtIndex = (idx) => {
    const s = normalizedSections[idx];
    if (!s) return null;
    return renderOneSection(s);
  };

  /* --------------------------- Output UI --------------------------- */

  if (loading) return (
    <>
      <HeroSkeleton />
    </>
  );
  if (notFound) return <div className="text-center py-5">Page Not Found</div>;

  const renderBlock = (block, used) => {
    const t = String(block || '').toLowerCase();
    if (t === 'hero') return <Hero />;
    if (t === 'sections') {
      return <>{normalizedSections.map((s, i) => (used && used.has(i) ? null : renderOneSection(s)))}</>;
    }
    if (/^sec:\d+$/.test(t)) {
      const id = parseInt(t.split(':')[1], 10);
      const idx = normalizedSections.findIndex((s) => Number(s.id) === id);
      if (idx >= 0) {
        const sec = normalizedSections[idx];
        if (sec && sec.type === 'testimonials' && Array.isArray(layoutOrder) && layoutOrder.some((x) => String(x).toLowerCase() === 'testimonials')) {
          if (used) used.add(idx);
          return null;
        }
        if (used) used.add(idx);
        return renderSectionAtIndex(idx);
      }
      return null;
    }
    if (/^section(-)?\d+$/.test(t)) {
      const n = parseInt(t.replace('section', '').replace('-', ''), 10);
      const idx = isNaN(n) ? -1 : (n - 1);
      if (idx >= 0) {
        if (used) used.add(idx);
        return renderSectionAtIndex(idx);
      }
      return null;
    }
    if (t === 'stats' && (data?.stats?.items?.length > 0 || Array.isArray(data?.stats))) return (
      <section><HomeStats stats={data?.stats} i18n={i18n} /></section>
    );
    if (t === 'testimonials' && ((data?.testimonials?.cards?.length > 0) || Array.isArray(data?.testimonials))) {
      const src = data?.testimonials || {};
      const cardsSrc = Array.isArray(src) ? src : (src.cards || []);
      const tIdx = normalizedSections.findIndex((s) => s.type === 'testimonials');
      const tSec = tIdx >= 0 ? normalizedSections[tIdx] : null;
      if (used && tIdx >= 0) used.add(tIdx);
      const titleFromSec = tSec ? (i18n.language === 'ar' ? tSec.payload.titleAr : tSec.payload.title) : undefined;
      const subtitleFromSec = tSec ? (i18n.language === 'ar' ? tSec.payload.subtitleAr : tSec.payload.subtitle) : undefined;
      const title = titleFromSec || (i18n.language === 'ar' ? (src.titleAr || src.title) : (src.title || src.titleAr));
      const subtitle = subtitleFromSec || (i18n.language === 'ar' ? (src.subtitleAr || src.subtitle) : (src.subtitle || src.subtitleAr));
      const tObj = {
        title,
        subtitle,
        cards: cardsSrc.map((c) => ({
          id: c.id,
          avatar: assetUrl(c.avatar || c.image || ''),
          name: i18n.language === 'ar'
            ? (c.lang?.title || c.title_ar || c.nameAr || c.name || '')
            : (c.lang?.title || c.title_en || c.name || c.nameAr || ''),
          role: i18n.language === 'ar'
            ? (c.lang?.subtitle || c.subtitle_ar || c.roleAr || c.role || '')
            : (c.lang?.subtitle || c.subtitle_en || c.role || c.roleAr || ''),
          comment: i18n.language === 'ar'
            ? (c.lang?.comment || c.comment_ar || c.description_ar || c.commentAr || c.comment || '')
            : (c.lang?.comment || c.comment_en || c.description_en || c.comment || c.commentAr || ''),
          companyname: c.company || c.companyname || '',
        })),
      };
      return (
        <section><HomeTestimonials testimonials={tObj} i18n={i18n} /></section>
      );
    }
    return null;
  };

  const layoutOrder = Array.isArray(data?.layout) && data.layout.length ? data.layout : ['hero', 'sections', 'stats', 'testimonials'];

  const usedIndices = new Set();
  const elements = layoutOrder.map((blk, idx) => (
    <React.Fragment key={`blk-${idx}`}>{renderBlock(blk, usedIndices)}</React.Fragment>
  ));

  return (
    <>
      {elements}

      <Helmet>
        {seo.title && <title>{seo.title}</title>}
        {seo.description && <meta name="description" content={seo.description} />}
        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      </Helmet>

      {/* <TopCards /> */}
    </>
  );
}

